import React, {
  createContext,
  useCallback,
  useState,
  useContext,
  useEffect,
} from 'react';
import api from '../services/api';
import HttpClient from '../services/utils/HttpClient';

interface User {
  id: number;
  cargo: string;
  acesso: string;
  email: string;
  nome: string;
  empresa: {
    lat: number;
    lon: number;
    time_zone?: string;
  };
  empresa_id: number;
  empresaprincipal: number;
  empresa_nome_fantasia: string;
  empresa_logo?: string;
  data_expiracao?: string;
  ativo: boolean;
}

interface AuthState {
  token: string;
  user: User;
}

interface SignCredentials {
  login: string;
  senha: string;
}

interface CaixaState {
  id: number;
  saldo_final: number;
  isOpen: boolean;
}

interface Rotina {
  listar: boolean;
  rotina: boolean;
  alterar: boolean;
  excluir: boolean;
  incluir: boolean;
}

interface AuthContextData {
  user: User;
  rotinas: Rotina[] | undefined;
  findRotinas: any;
  caixa: CaixaState;
  messageAlreadyView: boolean;
  setMessageAlreadyView(value: boolean): void;
  signIn(credentials: SignCredentials): Promise<void>;
  signOut(): void;
  updateUser(user: User): void;
  handleSetCaixaData(data: CaixaState): void;
  clearCaixaData(): void;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

const AuthProvider: React.FC = ({ children }) => {
  const [rotinas, setRotinas] = useState<Rotina[] | undefined>();

  const [messageAlreadyView, setMessageAlreadyView] = useState(false);
  const [data, setData] = useState<AuthState>(() => {
    const token = localStorage.getItem('@AtacDelivery:token');
    const user = localStorage.getItem('@AtacDelivery:user');

    if (token && user) {
      api.defaults.headers.Authorization = `Bearer ${token}`;
      HttpClient.setToken(token);

      return { token, user: JSON.parse(user) };
    }

    return {} as AuthState;
  });

  const [caixa, setCaixa] = useState<CaixaState>(() => {
    return {} as CaixaState;
  });

  const signIn = useCallback(async ({ login, senha }) => {
    const response = await api.post('/session', {
      login,
      senha,
    });

    const { token, user: userRequest } = response.data;

    if (!userRequest || !userRequest.id) {
      throw new Error('Resposta inválida do servidor');
    }

    const user: User = {
      id: userRequest.id,
      acesso: userRequest.acesso,
      cargo: userRequest.cargo || 'Administrador',
      email: userRequest.email,
      nome: userRequest.nome,
      empresa: userRequest.empresa || { lat: 0, lon: 0 },
      empresa_id: userRequest.empresa_id,
      empresaprincipal: userRequest.empresaprincipal,
      empresa_nome_fantasia: userRequest.empresa?.nome_fantasia || '',
      empresa_logo: userRequest.grupoEmpresa?.logomarca,
      data_expiracao: userRequest.empresa?.data_expiracao,
      ativo: userRequest.empresa?.ativo ?? true,
    };

    localStorage.setItem('@AtacDelivery:token', token);
    localStorage.setItem('@AtacDelivery:user', JSON.stringify(user));

    api.defaults.headers.Authorization = `Bearer ${token}`;
    HttpClient.setToken(token);

    setData({ token, user });
  }, []);

  const signOut = useCallback(() => {
    localStorage.removeItem('@AtacDelivery:token');
    localStorage.removeItem('@AtacDelivery:user');

    setData({} as AuthState);
    window.location.reload();
  }, []);

  const updateUser = useCallback(
    (user: User) => {
      localStorage.setItem('@AtacDelivery:user', JSON.stringify(user));

      setData({
        token: data.token,
        user,
      });
    },
    [setData, data.token],
  );

  const handleSetCaixaData = useCallback(
    ({ id, isOpen, saldo_final }: CaixaState) => {
      setCaixa({ id, isOpen, saldo_final });
    },
    [],
  );

  const clearCaixaData = useCallback(() => {
    setCaixa({} as CaixaState);
  }, []);

  const findRotinas = (rotina: string): any =>
    rotinas?.find((item: any) => item.rotina === rotina);

  useEffect(() => {
    if (data.user) {
      api
        .get(`acesso/${data.user.id}`)
        .then((res) => setRotinas(res.data.rotinas));
    }
  }, [data.user]);

  return (
    <AuthContext.Provider
      value={{
        user: data.user,
        rotinas,
        findRotinas,
        messageAlreadyView,
        caixa,
        signIn,
        signOut,
        updateUser,
        handleSetCaixaData,
        clearCaixaData,
        setMessageAlreadyView,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

function useAuth(): AuthContextData {
  const context = useContext(AuthContext);

  if (!context)
    throw new Error('useAuth deve ser utilizado junto do AuthProvider');

  return context;
}

export { AuthProvider, useAuth };
